import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Slider,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Divider,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ImageSearch as ImageSearchIcon,
  PostAdd as PostAddIcon,
  TextFields as TextFieldsIcon,
  Inventory as ContentPackIcon
} from '@mui/icons-material';
import { contentPackService } from '../services/contentPackService';
import {
  PackType,
  ExportFormat,
  SocialPlatform,
  ContentPackSession,
  ImagePrompt,
  PromptItem,
  SocialPost
} from '../models/contentPack';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

const ContentPackStudio = () => {
  const navigate = useNavigate();
  
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ContentPackSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ContentPackSession | null>(null);
  
  // Create dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createPackType, setCreatePackType] = useState<PackType>(PackType.Image);
  
  // Common create fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [suggestedPrice, setSuggestedPrice] = useState(0);
  
  // Image pack specific
  const [packTheme, setPackTheme] = useState('');
  const [style, setStyle] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('1920x1080');
  
  // Prompt pack specific
  const [targetModels, setTargetModels] = useState<string[]>([]);
  const [complexity, setComplexity] = useState('medium');
  
  // Social pack specific
  const [industry, setIndustry] = useState('');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([]);
  const [campaignDuration, setCampaignDuration] = useState(30);
  const [targetAudience, setTargetAudience] = useState('');
  
  // Generation settings
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);

  // Backend asset retrieval
  const {
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'ContentPackStudio',
    sessionBased: false,
    autoFetch: true,
  });

  useEffect(() => {
    loadSessions();
  }, []);
  
  const loadSessions = () => {
    const allSessions = contentPackService.getAllSessions();
    const sessionList = Object.values(allSessions);
    console.log('Loading sessions:', sessionList);
    setSessions(sessionList);
  };

  // Merge backend assets with local data
  useEffect(() => {
    const localData = Object.values(contentPackService.getAllSessions());

    if (backendAssets && backendAssets.length > 0) {
      const converted: ContentPackSession[] = backendAssets.map(asset => {
        const packData = asset.textContent ? JSON.parse(asset.textContent) : null;
        return {
          sessionId: asset.id,
          packType: packData?.packType || 'image',
          status: packData?.status || 'created',
          progress: packData?.progress || 0,
          packDetails: packData?.packDetails || {},
          createdAt: new Date(asset.createdAt).toISOString(),
          updatedAt: new Date(asset.updatedAt || asset.createdAt).toISOString()
        };
      });

      const backendIds = new Set(converted.map(s => s.sessionId));
      const localOnly = localData.filter(s => !backendIds.has(s.sessionId));
      setSessions([...converted, ...localOnly]);
    }
  }, [backendAssets]);
  
  const handleCreatePack = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      switch (createPackType) {
        case PackType.Image:
          response = await contentPackService.createImagePack({
            title,
            description,
            category,
            theme: packTheme,
            style,
            aspectRatio,
            resolution,
            suggestedPrice,
            tags
          });
          break;
          
        case PackType.Prompt:
          response = await contentPackService.createPromptPack({
            title,
            description,
            category,
            suggestedPrice,
            tags,
            targetModels,
            complexity
          });
          break;
          
        case PackType.Social:
          response = await contentPackService.createSocialPack({
            title,
            description,
            category,
            industry,
            suggestedPrice,
            tags,
            platforms,
            campaignDuration,
            targetAudience
          });
          break;
      }
      
      setCreateDialogOpen(false);
      resetCreateForm();
      
      // Create a proper session from the response
      if (response && response.sessionId) {
        console.log('Pack creation response:', response);
        
        // Handle actual API response format - prompts/posts may be at root level
        const responseAny = response as any;
        
        // Convert packType from API format to our enum format
        let packType = createPackType;
        if (responseAny.pack?.packType || responseAny.packType) {
          const apiPackType = responseAny.pack?.packType || responseAny.packType;
          console.log('API packType:', apiPackType, 'Original createPackType:', createPackType);
          if (apiPackType === 'image_pack' || apiPackType === 'image') {
            packType = PackType.Image;
          } else if (apiPackType === 'prompt_pack' || apiPackType === 'prompt') {
            packType = PackType.Prompt;
          } else if (apiPackType === 'social_pack' || apiPackType === 'social') {
            packType = PackType.Social;
          }
        }
        const promptsFromResponse = responseAny.prompts || responseAny.pack?.prompts || [];
        const postsFromResponse = responseAny.posts || responseAny.pack?.posts || [];
        const imagesFromResponse = responseAny.images || responseAny.pack?.images || [];
        
        let packDetails: any = {
          title: responseAny.pack?.title || responseAny.title || title,
          description: responseAny.pack?.description || responseAny.description || description,
          category: responseAny.pack?.category || responseAny.category || category,
          tags: responseAny.pack?.tags || responseAny.tags || tags,
          suggestedPrice: responseAny.pack?.price || responseAny.price || suggestedPrice,
        };

        // Add type-specific details
        if (createPackType === PackType.Image) {
          packDetails = {
            ...packDetails,
            imageCount: imagesFromResponse.length || 0,
            theme: responseAny.pack?.theme || packTheme,
            style: responseAny.pack?.style || style,
            aspectRatio: responseAny.pack?.dimensions?.aspectRatio || aspectRatio,
            // Convert string prompts to ImagePrompt objects
            prompts: promptsFromResponse.map((prompt: string | any, index: number) => ({
              id: `prompt-${index}`,
              prompt: typeof prompt === 'string' ? prompt : prompt.prompt,
              variations: [],
              metadata: typeof prompt === 'object' ? prompt : {}
            }))
          };
        } else if (createPackType === PackType.Prompt) {
          packDetails = {
            ...packDetails,
            promptCount: promptsFromResponse.length || 0,
            complexity: complexity,
            // Convert string prompts to PromptItem objects  
            prompts: promptsFromResponse.map((prompt: string | any, index: number) => ({
              id: `prompt-${index}`,
              title: `Prompt ${index + 1}`,
              prompt: typeof prompt === 'string' ? prompt : prompt.prompt,
              category: category,
              tags: tags
            }))
          };
        } else if (createPackType === PackType.Social) {
          packDetails = {
            ...packDetails,
            postCount: postsFromResponse.length || 0,
            platforms: platforms,
            posts: postsFromResponse
          };
        }

        const newSession: ContentPackSession = {
          sessionId: response.sessionId,
          packType,
          status: response.status || 'created',
          progress: response.status === 'prompts_generated' ? 100 : 0,
          packDetails,
          createdAt: new Date(responseAny.pack?.createdAt || responseAny.createdAt || Date.now()).toISOString(),
          updatedAt: new Date(responseAny.pack?.updatedAt || responseAny.updatedAt || Date.now()).toISOString()
        };
        
        // Save the new session
        contentPackService.saveSession(newSession);
        console.log('Session saved:', newSession);
        console.log('Prompts in saved session:', (newSession.packDetails as any)?.prompts || []);
        
        // Update state
        setSessions(prev => {
          const updated = [newSession, ...prev];
          console.log('Sessions updated:', updated);
          console.log('First session prompts count:', (updated[0].packDetails as any)?.prompts?.length || 0);
          return updated;
        });
        setSelectedSession(newSession);
        setActiveTab(getTabIndexForPackType(newSession.packType));
      } else {
        // Fallback to loading sessions if response format is unexpected
        loadSessions();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create content pack');
    } finally {
      setLoading(false);
    }
  };
  
  const resetCreateForm = () => {
    setTitle('');
    setDescription('');
    setCategory('');
    setTags([]);
    setTagInput('');
    setSuggestedPrice(0);
    setPackTheme('');
    setStyle('');
    setTargetModels([]);
    setIndustry('');
    setPlatforms([]);
    setCampaignDuration(30);
    setTargetAudience('');
  };
  
  const getTabIndexForPackType = (packType: PackType): number => {
    switch (packType) {
      case PackType.Image: return 0;
      case PackType.Prompt: return 1;
      case PackType.Social: return 2;
      default: return 0;
    }
  };
  
  const handleGenerateContent = async () => {
    if (!selectedSession) return;
    
    setLoading(true);
    setError(null);
    
    try {
      switch (selectedSession.packType) {
        case PackType.Image:
          await contentPackService.generatePrompts(selectedSession.sessionId, {
            count: 10,
            includeVariations: true,
            temperature,
            maxTokens
          });
          break;
          
        case PackType.Social:
          await contentPackService.generatePosts(selectedSession.sessionId, {
            platforms: (selectedSession.packDetails as any).platforms || [],
            postsPerPlatform: 5,
            includeHashtags: true,
            temperature
          });
          break;
      }
      
      // Reload session
      const updatedSession = contentPackService.getSession(selectedSession.sessionId);
      if (updatedSession) {
        setSelectedSession(updatedSession);
        // Also update the sessions list to reflect the changes
        setSessions(prev => prev.map(s => 
          s.sessionId === selectedSession.sessionId ? updatedSession : s
        ));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportPack = async (format: ExportFormat) => {
    if (!selectedSession) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (format === ExportFormat.Markdown) {
        const markdown = await contentPackService.exportMarkdown(selectedSession.sessionId);
        downloadTextFile(markdown, `${selectedSession.packDetails.title}.md`);
      } else if (format === ExportFormat.Text) {
        const text = await contentPackService.exportText(selectedSession.sessionId);
        downloadTextFile(text, `${selectedSession.packDetails.title}.txt`);
      } else {
        const blob = await contentPackService.downloadPack(selectedSession.sessionId);
        downloadBlob(blob, `${selectedSession.packDetails.title}.zip`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export pack');
    } finally {
      setLoading(false);
    }
  };
  
  const downloadTextFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    downloadBlob(blob, filename);
  };
  
  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const handleDeleteSession = (sessionId: string) => {
    contentPackService.deleteSession(sessionId);
    loadSessions();
    if (selectedSession?.sessionId === sessionId) {
      setSelectedSession(null);
    }
  };
  
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };
  
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };
  
  const getPackTypeIcon = (packType: PackType) => {
    switch (packType) {
      case PackType.Image: return <ImageSearchIcon />;
      case PackType.Prompt: return <TextFieldsIcon />;
      case PackType.Social: return <PostAddIcon />;
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexWrap: 'wrap',
            gap: { xs: 0.5, sm: 1 }, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
          }}
        >
          <ContentPackIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Content Pack Studio
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 1, sm: 0 },
          }}
        >
          Create and manage AI content packs for images, prompts, and social media campaigns
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: { xs: 2, sm: 3 },
            py: { xs: 0.5, sm: 1 },
          }} 
          onClose={() => setError(null)}
        >
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {error}
          </Typography>
        </Alert>
      )}

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, value) => setActiveTab(value)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
              minWidth: { xs: 80, sm: 100 },
              minHeight: { xs: 44, sm: 48 },
            }
          }}
        >
          <Tab 
            label={isMobile ? `Image (${sessions.filter(s => s.packType === PackType.Image).length})` : `Image Packs (${sessions.filter(s => s.packType === PackType.Image).length})`}
          />
          <Tab 
            label={isMobile ? `Prompt (${sessions.filter(s => s.packType === PackType.Prompt).length})` : `Prompt Packs (${sessions.filter(s => s.packType === PackType.Prompt).length})`}
          />
          <Tab 
            label={isMobile ? `Social (${sessions.filter(s => s.packType === PackType.Social).length})` : `Social Packs (${sessions.filter(s => s.packType === PackType.Social).length})`}
          />
        </Tabs>
      </Paper>
      
      {/* Image Packs Tab */}
      <TabPanel value={activeTab} index={0}>
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'row', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'center', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1.5, sm: 0 },
          }}>
            <Typography 
              variant="h5"
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Image Content Packs
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => {
                setCreatePackType(PackType.Image);
                setCreateDialogOpen(true);
              }}
            >
             { isMobile ? '+' : 'Create Image Pack'}
            </Button>
          </Box>
          
          {sessions.filter(s => s.packType === PackType.Image).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
              <ImageIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                No Image Packs Created
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 2, sm: 0 },
                }}
              >
                Create AI-generated image prompt collections for various themes and styles
              </Typography>
              <Button
                variant="outlined"
                startIcon={!isMobile && <AddIcon />}
                onClick={() => {
                  setCreatePackType(PackType.Image);
                  setCreateDialogOpen(true);
                }}
                size={isMobile ? 'medium' : 'large'}
              >
                Create Your First Image Pack
              </Button>
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {sessions.filter(s => s.packType === PackType.Image).map(session => (
                <Grid item xs={12} md={6} key={session.sessionId}>
                  <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                      <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="start" 
                        sx={{ mb: { xs: 1.5, sm: 2 } }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="h6"
                            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                          >
                            {session.packDetails?.title || 'Untitled Pack'}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 1,
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            }}
                          >
                            {session.packDetails?.description || 'No description available'}
                          </Typography>
                          {session.packDetails?.category && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              Category: {session.packDetails.category}
                            </Typography>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteSession(session.sessionId)}
                          sx={{
                            minWidth: 44,
                            minHeight: 44,
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                      
                      <Stack 
                        direction="row" 
                        spacing={{ xs: 0.5, sm: 1 }} 
                        sx={{ mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}
                      >
                        <Chip 
                          label={`${(session.packDetails as any)?.prompts?.length || (session.packDetails as any)?.imageCount || 0} prompts`} 
                          size="small"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                        <Chip 
                          label={session.status || 'pending'} 
                          size="small" 
                          color={
                            session.status === 'completed' || session.status === 'prompts_generated' ? 'success' : 
                            session.status === 'created' ? 'info' :
                            session.status === 'active' ? 'warning' : 'default'
                          }
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                        {(session.packDetails as any)?.aspectRatio && (
                          <Chip 
                            label={(session.packDetails as any).aspectRatio} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                        )}
                        {(session.packDetails as any)?.theme && (
                          <Chip 
                            label={`Theme: ${(session.packDetails as any).theme}`} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          />
                        )}
                      </Stack>
                      
                      {session.progress > 0 && session.progress < 100 && (
                        <LinearProgress variant="determinate" value={session.progress} sx={{ mb: { xs: 1.5, sm: 2 } }} />
                      )}
                    </CardContent>
                    <CardActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
                      <Button 
                        size="small" 
                        onClick={() => setSelectedSession(session)}
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Open
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleExportPack(ExportFormat.ZIP)}
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Export
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </TabPanel>
      
      {/* Prompt Packs Tab */}
      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'row', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'center', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1.5, sm: 0 },
          }}>
            <Typography 
              variant="h5"
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Prompt Content Packs
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => {
                setCreatePackType(PackType.Prompt);
                setCreateDialogOpen(true);
              }}
            >
              {isMobile ? '+' : 'Create Prompt Pack'}
            </Button>
          </Box>
          
          {sessions.filter(s => s.packType === PackType.Prompt).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
              <TextFieldsIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                No Prompt Packs Created
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 2, sm: 0 },
                }}
              >
                Create curated collections of AI prompts for different models and use cases
              </Typography>
              <Button
                variant="outlined"
                startIcon={!isMobile && <AddIcon />}
                onClick={() => {
                  setCreatePackType(PackType.Prompt);
                  setCreateDialogOpen(true);
                }}
                size={isMobile ? 'medium' : 'large'}
              >
                Create Your First Prompt Pack
              </Button>
            </Box>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {sessions.filter(s => s.packType === PackType.Prompt).map(session => (
                <Grid item xs={12} md={6} key={session.sessionId}>
                  <Card>
                    <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                      <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="start" 
                        sx={{ mb: { xs: 1.5, sm: 2 } }}
                      >
                        <Box>
                          <Typography 
                            variant="h6"
                            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                          >
                            {session.packDetails?.title || 'Untitled Pack'}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            {session.packDetails?.description || 'No description available'}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteSession(session.sessionId)}
                          sx={{
                            minWidth: 44,
                            minHeight: 44,
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                      
                      <Stack 
                        direction="row" 
                        spacing={{ xs: 0.5, sm: 1 }} 
                        sx={{ mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}
                      >
                        <Chip 
                          label={`${(session.packDetails as any)?.prompts?.length || (session.packDetails as any)?.promptCount || 0} prompts`} 
                          size="small"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                        <Chip 
                          label={session.status || 'pending'} 
                          size="small" 
                          color={
                            session.status === 'completed' || session.status === 'prompts_generated' ? 'success' : 
                            session.status === 'created' ? 'info' :
                            session.status === 'active' ? 'warning' : 'default'
                          }
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                        <Chip 
                          label={(session.packDetails as any)?.complexity || 'medium'} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                      </Stack>
                    </CardContent>
                    <CardActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
                      <Button 
                        size="small" 
                        onClick={() => setSelectedSession(session)}
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Open
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleExportPack(ExportFormat.Markdown)}
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Export
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </TabPanel>
      
      {/* Social Packs Tab */}
      <TabPanel value={activeTab} index={2}>
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'row', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'center', sm: 'center' }, 
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1.5, sm: 0 },
          }}>
            <Typography 
              variant="h5"
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Social Media Content Packs
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => {
                setCreatePackType(PackType.Social);
                setCreateDialogOpen(true);
              }}
            >
             {isMobile ? '+' : 'Create Social Pack'}
            </Button>
          </Box>
          
          {sessions.filter(s => s.packType === PackType.Social).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
              <ShareIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1.5, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                No Social Packs Created
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 2, sm: 0 },
                }}
              >
                Create comprehensive social media campaigns with posts for multiple platforms
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreatePackType(PackType.Social);
                  setCreateDialogOpen(true);
                }}
              >
                Create Your First Social Pack
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {sessions.filter(s => s.packType === PackType.Social).map(session => (
                <Grid item xs={12} md={6} key={session.sessionId}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                        <Box>
                          <Typography variant="h6">{session.packDetails?.title || 'Untitled Pack'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {session.packDetails?.description || 'No description available'}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteSession(session.sessionId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                      
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip label={`${(session.packDetails as any)?.postCount || 0} posts`} size="small" />
                        {(session.packDetails as any)?.platforms?.map((platform: string) => (
                          <Chip key={platform} label={platform} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        onClick={() => setSelectedSession(session)}
                      >
                        Open
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleExportPack(ExportFormat.JSON)}
                      >
                        Export
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </TabPanel>
      
      {/* Create Pack Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        PaperProps={{
          sx: {
            position: 'fixed',
            margin: 0,
            maxWidth: 'md',
            width: 'calc(100% - 48px)',
            top: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            '@media (min-width: 960px)': {
              width: '900px',
            }
          }
        }}
      >
        <DialogTitle>Create New Content Pack</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Pack Type</InputLabel>
              <Select
                value={createPackType}
                onChange={(e) => setCreatePackType(e.target.value as PackType)}
                label="Pack Type"
              >
                <MenuItem value={PackType.Image}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ImageIcon />
                    <span>Image Pack - AI-generated image prompts</span>
                  </Stack>
                </MenuItem>
                <MenuItem value={PackType.Prompt}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DescriptionIcon />
                    <span>Prompt Pack - Curated AI prompts</span>
                  </Stack>
                </MenuItem>
                <MenuItem value={PackType.Social}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ShareIcon />
                    <span>Social Pack - Social media campaigns</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Add Tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleAddTag} disabled={!tagInput.trim()}>
                      <AddIcon />
                    </IconButton>
                  )
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                {tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Stack>
            </Box>
            
            <TextField
              fullWidth
              type="number"
              label="Suggested Price ($)"
              value={suggestedPrice}
              onChange={(e) => setSuggestedPrice(Number(e.target.value))}
              sx={{ mb: 2 }}
            />
            
            {/* Pack-specific fields */}
            {createPackType === PackType.Image && (
              <>
                <TextField
                  fullWidth
                  label="Theme"
                  value={packTheme}
                  onChange={(e) => setPackTheme(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Style"
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Aspect Ratio</InputLabel>
                  <Select
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    label="Aspect Ratio"
                  >
                    <MenuItem value="16:9">16:9 (Widescreen)</MenuItem>
                    <MenuItem value="4:3">4:3 (Standard)</MenuItem>
                    <MenuItem value="1:1">1:1 (Square)</MenuItem>
                    <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            
            {createPackType === PackType.Prompt && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Target Models</InputLabel>
                  <Select
                    multiple
                    value={targetModels}
                    onChange={(e) => setTargetModels(e.target.value as string[])}
                    label="Target Models"
                    renderValue={(selected) => (
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Stack>
                    )}
                  >
                    <MenuItem value="GPT-4">GPT-4</MenuItem>
                    <MenuItem value="GPT-3.5">GPT-3.5</MenuItem>
                    <MenuItem value="Claude">Claude</MenuItem>
                    <MenuItem value="Claude-Instant">Claude Instant</MenuItem>
                    <MenuItem value="Gemini">Gemini</MenuItem>
                    <MenuItem value="Gemini-Pro">Gemini Pro</MenuItem>
                    <MenuItem value="Llama-2">Llama 2</MenuItem>
                    <MenuItem value="Mistral">Mistral</MenuItem>
                    <MenuItem value="DALL-E">DALL-E</MenuItem>
                    <MenuItem value="Midjourney">Midjourney</MenuItem>
                    <MenuItem value="Stable-Diffusion">Stable Diffusion</MenuItem>
                    <MenuItem value="Flux">Flux</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Complexity</InputLabel>
                  <Select
                    value={complexity}
                    onChange={(e) => setComplexity(e.target.value)}
                    label="Complexity"
                  >
                    <MenuItem value="simple">Simple</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="advanced">Advanced</MenuItem>
                    <MenuItem value="expert">Expert</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            
            {createPackType === PackType.Social && (
              <>
                <TextField
                  fullWidth
                  label="Industry"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Platforms</InputLabel>
                  <Select
                    multiple
                    value={platforms}
                    onChange={(e) => setPlatforms(e.target.value as SocialPlatform[])}
                    label="Platforms"
                  >
                    {Object.values(SocialPlatform).map(platform => (
                      <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  type="number"
                  label="Campaign Duration (days)"
                  value={campaignDuration}
                  onChange={(e) => setCampaignDuration(Number(e.target.value))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Target Audience"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreatePack} 
            variant="contained"
            disabled={loading || !title.trim() || !description.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            Create Pack
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Selected Session Detail */}
      {selectedSession && (
        <Dialog
          open={!!selectedSession}
          onClose={() => {
            setSelectedSession(null);
            // Refresh sessions when dialog closes to ensure latest data is shown
            loadSessions();
          }}
          PaperProps={{
            sx: {
              position: 'fixed',
              margin: 0,
              maxWidth: 'md',
              width: 'calc(100% - 48px)',
              top: '10%',
              left: '50%',
              transform: 'translateX(-50%)',
              '@media (min-width: 960px)': {
                width: '900px',
              }
            }
          }}
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center">
                {getPackTypeIcon(selectedSession.packType)}
                <Typography variant="h6">{selectedSession.packDetails?.title || 'Untitled Pack'}</Typography>
              </Stack>
              <IconButton onClick={() => setSelectedSession(null)}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              {selectedSession.packDetails?.description || 'No description available'}
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={handleGenerateContent}
                disabled={loading}
              >
                Generate Content
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExportPack(ExportFormat.ZIP)}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={async () => {
                  try {
                    const response = await contentPackService.getPackStatus(selectedSession.sessionId);
                    // Update the selected session with the new status data
                    if (response) {
                      // Handle actual API response format
                      const responseAny = response as any;
                      const promptsFromResponse = responseAny.prompts || responseAny.pack?.prompts || [];
                      const postsFromResponse = responseAny.posts || responseAny.pack?.posts || [];
                      const imagesFromResponse = responseAny.images || responseAny.pack?.images || [];

                      const updatedSession = {
                        ...selectedSession,
                        status: response.status || selectedSession.status,
                        progress: response.status === 'prompts_generated' ? 100 : (response.progress || selectedSession.progress),
                        packDetails: {
                          ...selectedSession.packDetails,
                          title: responseAny.title || selectedSession.packDetails?.title || 'Untitled Pack',
                          description: responseAny.description || selectedSession.packDetails?.description,
                          category: responseAny.category || selectedSession.packDetails?.category,
                          tags: responseAny.tags || selectedSession.packDetails?.tags,
                          // Update image-specific details
                          ...(selectedSession.packType === PackType.Image && {
                            imageCount: promptsFromResponse.length || (selectedSession.packDetails as any)?.imageCount || 0,
                            theme: responseAny.theme || (selectedSession.packDetails as any)?.theme,
                            style: responseAny.style || (selectedSession.packDetails as any)?.style,
                            prompts: promptsFromResponse.length > 0 ? promptsFromResponse.map((prompt: string | any, index: number) => ({
                              id: `prompt-${index}`,
                              prompt: typeof prompt === 'string' ? prompt : prompt.prompt,
                              variations: [],
                              metadata: typeof prompt === 'object' ? prompt : {}
                            })) : (selectedSession.packDetails as any)?.prompts || [],
                          }),
                          // Update prompt-specific details
                          ...(selectedSession.packType === PackType.Prompt && {
                            promptCount: promptsFromResponse.length || (selectedSession.packDetails as any)?.promptCount || 0,
                            prompts: promptsFromResponse.length > 0 ? promptsFromResponse.map((prompt: string | any, index: number) => ({
                              id: `prompt-${index}`,
                              title: `Prompt ${index + 1}`,
                              prompt: typeof prompt === 'string' ? prompt : prompt.prompt,
                              category: (selectedSession.packDetails as any)?.category,
                              tags: (selectedSession.packDetails as any)?.tags
                            })) : (selectedSession.packDetails as any)?.prompts || [],
                          }),
                          // Update social-specific details
                          ...(selectedSession.packType === PackType.Social && {
                            postCount: postsFromResponse.length || (selectedSession.packDetails as any)?.postCount || 0,
                            posts: postsFromResponse.length > 0 ? postsFromResponse : (selectedSession.packDetails as any)?.posts || [],
                          }),
                        }
                      };
                      setSelectedSession(updatedSession);
                      
                      // Also update in the sessions list
                      setSessions(prev => prev.map(s => 
                        s.sessionId === selectedSession.sessionId ? updatedSession : s
                      ));
                      
                      // Save to localStorage
                      contentPackService.saveSession(updatedSession);
                    }
                  } catch (error) {
                    console.error('Failed to refresh status:', error);
                  }
                }}
              >
                Refresh Status
              </Button>
            </Stack>
            
            {selectedSession.packType === PackType.Image && (
              <Box>
                <Typography variant="h6" gutterBottom>Generated Prompts</Typography>
                <List>
                  {((selectedSession.packDetails as any)?.prompts || []).map((prompt: ImagePrompt) => (
                    <ListItem key={prompt.id}>
                      <ListItemText
                        primary={prompt.prompt}
                        secondary={prompt.variations?.join(' | ')}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => navigator.clipboard.writeText(prompt.prompt)}>
                          <CopyIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {selectedSession.packType === PackType.Prompt && (
              <Box>
                <Typography variant="h6" gutterBottom>Generated Prompts</Typography>
                <List>
                  {((selectedSession.packDetails as any)?.prompts || []).map((prompt: any, index: number) => (
                    <ListItem key={prompt.id || index}>
                      <ListItemText
                        primary={prompt.title || `Prompt ${index + 1}`}
                        secondary={prompt.prompt}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => navigator.clipboard.writeText(prompt.prompt)}>
                          <CopyIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            {selectedSession.packType === PackType.Social && (
              <Box>
                <Typography variant="h6" gutterBottom>Generated Posts</Typography>
                <Grid container spacing={2}>
                  {((selectedSession.packDetails as any)?.posts || []).map((post: SocialPost) => (
                    <Grid item xs={12} md={6} key={post.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Chip label={post.platform} size="small" sx={{ mb: 1 }} />
                          <Typography variant="body1" paragraph>
                            {post.content}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {(post.hashtags || []).map(tag => (
                              <Chip key={tag} label={`#${tag}`} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
};

export default ContentPackStudio;